import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import TableIcon from '@mui/icons-material/TableRestaurant';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TopAppBar from '../layout/TopAppBar';
import { ActionButton, PageContainer, SectionHeader, SurfaceCard } from '../ui/ConvivialComponents';
import { getGroupsForUser } from '../../services/firebaseData';
import { requestNativeLogin } from '../../services/nativeAppBridge';

export default function TablesPage() {
    const navigate = useNavigate();
    const { user_id: requestedUserId } = useParams();
    const currentUserId = localStorage.getItem('user_id');
    const targetUserId = requestedUserId || currentUserId;
    const isCurrentUser = targetUserId === currentUserId;
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!targetUserId) {
            const handledByNative = requestNativeLogin('/user/tables');

            if (!handledByNative) {
                navigate('/login', { state: { redirectTo: '/user/tables' } });
            }
        }
    }, [navigate, targetUserId]);

    useEffect(() => {
        if (!targetUserId) return;
        const fetchGroups = async () => {
            setLoading(true);
            try {
                const data = await getGroupsForUser(targetUserId, currentUserId);
                setGroups(data);
            } catch (error) {
                console.error('Error fetching groups:', error);
                setGroups([]);
            } finally {
                setLoading(false);
            }
        };

        fetchGroups();
    }, [targetUserId, currentUserId]);

    const sortedGroups = useMemo(() => {
        return [...groups].sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime());
    }, [groups]);

    const handleCreateTable = () => {
        localStorage.removeItem('searchedPlace');
        localStorage.removeItem('lastQueryPlace');
        localStorage.removeItem('tableVenue');
        navigate('/table');
    };

    return (
        <>
            <TopAppBar title="ConvivialApp.it" variant="back" showAvatar={!!currentUserId} />
            <PageContainer maxWidth="980px" sx={{ pt: 'calc(var(--cv-top-bar-height) + 32px)' }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h1" sx={{ color: 'primary.main', fontSize: { xs: 28, md: 34 } }}>
                            {isCurrentUser ? 'I miei tavoli' : 'Tavoli del profilo'}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 620 }}>
                            Storico dei tavoli creati o frequentati da questo profilo.
                        </Typography>
                    </Box>

                    <SurfaceCard>
                        <SectionHeader icon={<TableIcon />} title="Partecipazioni" />
                        {loading ? (
                            <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : sortedGroups.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="h3" sx={{ color: 'primary.main' }}>
                                    Nessun tavolo trovato
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                    Quando questo profilo partecipera a un tavolo, lo vedrai qui.
                                </Typography>
                            </Box>
                        ) : (
                            <Stack spacing={1.5}>
                                {sortedGroups.map((group) => (
                                    <TableHistoryRow
                                        key={group.id}
                                        group={group}
                                        onClick={() => navigate('/table/' + group.id)}
                                    />
                                ))}
                            </Stack>
                        )}
                    </SurfaceCard>

                    {isCurrentUser && (
                        <ActionButton variant="container" onClick={handleCreateTable} sx={{ maxWidth: 520, mx: 'auto' }}>
                            Organizza nuovo tavolo
                        </ActionButton>
                    )}
                </Stack>
            </PageContainer>
        </>
    );
}

function tableStatus(group) {
    const now = Date.now();
    const start = new Date(group.startAt).getTime();
    const end = new Date(group.endAt).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return { label: 'Da confermare', color: 'text.secondary' };
    if (end < now) return { label: 'Concluso', color: 'error.main' };
    if (start <= now && end >= now) return { label: 'In corso', color: 'success.main' };
    return { label: 'Programmato', color: 'secondary.main' };
}

function TableHistoryRow({ group, onClick }) {
    const status = tableStatus(group);
    const start = new Date(group.startAt);

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') onClick();
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(223,191,193,0.35)',
                bgcolor: 'background.surfaceLow',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'background.surfaceHigh' },
            }}
        >
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'primary.fixed', color: 'primary.main', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <TableIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Typography variant="h4" sx={{ fontSize: 17 }}>
                        {group.name || 'Tavolo conviviale'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: status.color, fontWeight: 600 }}>
                        {status.label}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                    <InfoPill icon={<LocationOnIcon fontSize="small" />} text={group.location?.place || 'Luogo non disponibile'} />
                    <InfoPill
                        icon={<CalendarMonthIcon fontSize="small" />}
                        text={Number.isNaN(start.getTime()) ? 'Data non disponibile' : start.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    />
                </Stack>
            </Box>
            <Button sx={{ minWidth: 42, color: 'primary.main' }}>
                <ChevronRightIcon />
            </Button>
        </Box>
    );
}

function InfoPill({ icon, text }) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary', minWidth: 0 }}>
            {icon}
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {text}
            </Typography>
        </Stack>
    );
}
